import React, {FunctionComponent, Reducer, useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useState} from "react";
import {
    QueryBuilderColumn,
    QueryBuilderQuery,
    QueryBuilderReducerAction,
    QueryBuilderReducerActionTypeEnum,
    QueryBuilderReducerDispatch,
    QueryBuilderReducerInitialState,
    QueryBuilderState,
} from "../index";
import QueryBuilderReducer from "../core/services/reducer";
import {Button, Card, Paper, TextField, TextFieldProps, Unstable_Grid2 as Grid, useMediaQuery, useTheme} from "@mui/material";
import QueryBuilderUtils from "../core/services/utils";
import QueryBuilderSaveTemplate from "./components/save-template";
import QueryBuilderFooter from "./components/footer";
import QueryBuilderCondition from "./components/condition";


export interface QueryBuilderProps {
    columns?: QueryBuilderColumn[];
    query?: QueryBuilderQuery;
    name?: string;
    hideKeywords?: boolean;
    hideConditions?: boolean;
    disabled?: boolean;
    loading?: boolean;
    search?: (query: QueryBuilderQuery) => void | Promise<void>;
    dataGridId?: string;
}

const QueryBuilder: FunctionComponent<QueryBuilderProps> = (props) => {
    const {search} = props;

    const [searchPending, setSearchPending] = useState(false);
    const [keywordFocused, setKeywordFocused] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const initialState = useMemo<QueryBuilderReducerInitialState>(() => ({
        columns: props.columns ?? [],
        query: props.query,
    }), [props.columns, props.query])

    const [state, _dispatch] = useReducer<Reducer<QueryBuilderState, QueryBuilderReducerAction>, QueryBuilderReducerInitialState>(
        QueryBuilderReducer.reducer,
        initialState,
        QueryBuilderReducer.init
    );
    const dispatch = useMemo<QueryBuilderReducerDispatch>(() => _dispatch, [])

    const disabled = useMemo(() => (props.disabled || props.loading) ?? false, [props.disabled, props.loading]);

    /**
     * With each change in the [keywordFocused] state:
     * - adds/removes the [keyword-focused] class to the [thead] element.
     */
    useEffect(() => {
        if (!props.dataGridId)
            return;
        const columnHeaders = document.querySelectorAll<HTMLTableHeaderCellElement>(`#${props.dataGridId} .data-grid-header-title`);
        for (const columnHeader of columnHeaders ?? []) {
            const columnName = columnHeader.dataset.columnName;
            if (!columnName)
                continue;
            const column = props.columns?.find(c => c.column === columnName);
            if (column?.isIncludedInKeywordSearch)
                columnHeader.classList.toggle('keyword-focused', keywordFocused);
        }

    }, [keywordFocused, props.columns, props.dataGridId]);

    /**
     * With each change in the [columns] prop:
     * - dispatches an action into the reducer to update the columns.
     */
    useLayoutEffect(() => {
        dispatch({
            type: QueryBuilderReducerActionTypeEnum.SetColumns,
            payload: {
                columns: props.columns ?? [],
            }
        })
    }, [dispatch, props.columns]);

    /**
     * With each change in the [query] or [columns] prop:
     * - dispatches an action into the reducer to update the query.
     */
    useEffect(() => {
        dispatch({
            type: QueryBuilderReducerActionTypeEnum.SetQuery,
            payload: {
                query: props.query ?? QueryBuilderReducer.placeholderQuery,
                columns: props.columns,
            },
        })
    }, [dispatch, props.query, props.columns]);

    /**
     * Dispatches an action into the reducer to update the keyword.
     */
    const onKeywordChanged = useCallback<Required<TextFieldProps>['onChange']>((e) => {
        dispatch({
            type: QueryBuilderReducerActionTypeEnum.SetKeyword,
            payload: {
                keyword: e.target.value,
            }
        })
    }, [dispatch]);

    /**
     * invokes the search function with the current query.
     */
    const onSearchClicked = useCallback(async (e?: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>) => {
        e?.preventDefault();
        await search?.(QueryBuilderUtils.siftQueryWithColumns(state.query, state.columns, QueryBuilderUtils.mapStateQueryRuleToQueryRule));
    }, [search, state.query, state.columns]);

    /**
     * When the [searchPending] state is true, the search is invoked.
     * This is used to invoke the search when the user presses the [Enter] key.
     */
    useEffect(() => {
        if (searchPending) {
            setSearchPending(false);
            onSearchClicked().then();
        }
    }, [searchPending, onSearchClicked]);

    /**
     * When the user presses the [Enter] key, the search is invoked.
     */
    const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === 'Enter') {
            for (const focusedElement of document.querySelectorAll(':focus')) {
                if (focusedElement instanceof HTMLElement)
                    focusedElement.blur();
            }
            setSearchPending(true);
        }
    }, [])

    return (
        <>
            <Card
                onKeyDown={onKeyDown}
                variant={'outlined'}
                sx={{
                    marginBlock: 2,
                    padding: 2,
                    backgroundColor: 'background.default',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    border: '1px solid transparent',
                    borderColor: 'divider',
                }}
            >
                <Paper sx={{padding: 2, display: 'flex', flexDirection: 'column', gap: 2}}>
                    <Grid container spacing={2}>
                        <Grid xs={12} md>
                            {
                                !props.hideKeywords &&
                                <TextField
                                    label="Keywords"
                                    variant="outlined"
                                    size={'small'}
                                    fullWidth
                                    value={state.query.keyword}
                                    onFocus={() => setKeywordFocused(true)}
                                    onBlur={() => setKeywordFocused(false)}
                                    onChange={onKeywordChanged}
                                />
                            }
                        </Grid>
                        {
                            props.name && !!state.query.filters.length && isMobile &&
                            <Grid xs={12} sx={{display: 'flex', justifyContent: 'flex-end'}}>
                                <QueryBuilderSaveTemplate
                                    name={props.name}
                                    query={state.query}
                                    columns={state.columns}
                                />
                            </Grid>
                        }
                        <Grid xs={12} md={'auto'}>
                            <Button
                                variant={'contained'}
                                color={'primary'}
                                disabled={disabled}
                                onClick={onSearchClicked}
                                size={'medium'}
                                sx={{
                                    paddingInline: 2,
                                    minWidth: isMobile ? `min(200px, 100%)` : 200,
                                    display: 'flex',
                                    marginInlineStart: 'auto'
                                }}
                            >
                                {
                                    props.loading
                                        ? "Searching..."
                                        : "Search"
                                }
                            </Button>
                        </Grid>
                    </Grid>
                    {
                        props.name && !!state.query.filters.length && !isMobile &&
                        <Grid container spacing={2} sx={{marginBlockEnd: 1}}>
                            <Grid xs={0} md/>
                            <Grid xs={12} md={'auto'}>
                                <QueryBuilderSaveTemplate
                                    name={props.name}
                                    query={state.query}
                                    columns={state.columns}
                                />
                            </Grid>
                        </Grid>
                    }
                </Paper>
                {
                    !props.hideConditions &&
                    state.query.filters.map((filter, conditionIndex) => (
                        <QueryBuilderCondition
                            key={`${filter}-${conditionIndex}`}
                            condition={filter}
                            disabled={disabled}
                            columns={state.columns}
                            conditionIndex={conditionIndex}
                            dispatch={dispatch}
                        />
                    ))
                }
                <QueryBuilderFooter
                    name={props.name}
                    hideConditions={props.hideConditions}
                    dispatch={dispatch}
                />
            </Card>
        </>
    );
}

export default QueryBuilder;
